import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Button, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/useAuth';
import { getAllAgents } from '../../services/DataService';
import { useAppDispatch } from '../../store/hooks';
import { setSnackbarOpen } from '../../store/slices/snackbar.slice';

type AdminAgent = {
  gcpAgentId: string;
  gcpName?: string | null;
  clientName?: string | null;
  messengerEnabled?: boolean | string | null;
};

export function AdminPage() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [agents, setAgents] = useState<AdminAgent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const isEnabled = useCallback((val: AdminAgent['messengerEnabled']) => {
    // Accept both boolean and string values robustly
    if (val === true || val === 'true') return true;
    return false;
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const res = await getAllAgents(token);
        setAgents((res?.data ?? []) as AdminAgent[]);
      } catch (error: any) {
        if (error?.response?.status === 401) {
          logout();
        } else {
          dispatch(setSnackbarOpen({ snackbarMessage: 'Fehler beim Laden der Agents.', snackbarSeverity: 'error' }));
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, logout, dispatch]);

  const sortedAgents = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const val = (s?: string | null) => (s ?? '').toString();
    return [...(agents || [])].sort((a, b) => {
      const c = collator.compare(val(a.clientName), val(b.clientName));
      if (c !== 0) return c;
      return collator.compare(val(a.gcpName), val(b.gcpName));
    });
  }, [agents]);

  const onOpenActivation = (gcpAgentId: string) => {
    navigate(`/messenger-activation/${gcpAgentId}`);
  };

  return (
    <Box p={3}>
      <Stack spacing={2}>
        <Typography variant="h5">Administration</Typography>
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Messenger aktiviert</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4}>Laden...</TableCell>
                  </TableRow>
                ) : sortedAgents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>Keine Agents gefunden.</TableCell>
                  </TableRow>
                ) : (
                  sortedAgents.map((a) => (
                    <TableRow key={a.gcpAgentId} hover>
                      <TableCell>{a.clientName ?? '-'}</TableCell>
                      <TableCell>{a.gcpName ?? '-'}</TableCell>
                      <TableCell>{isEnabled(a.messengerEnabled) ? 'true' : 'false'}</TableCell>
                      <TableCell align="right">
                        {!isEnabled(a.messengerEnabled) && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => onOpenActivation(a.gcpAgentId)}
                            disabled={!a.gcpAgentId}
                          >
                            Aktivieren
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Box>
  );
}

export default AdminPage;
